import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { ConfirmationDialog } from "../components/ConfirmationDialog";
import { defaultContentAPI } from "../api";
import { ImageUrlField } from "../components/ImageUrlField";
import type {
  DefaultContentSettings,
  DefaultContentSettingsUpdate,
} from "../api";
import { useAuthStore } from "../store/authStore";
import {
  Loader2,
  RotateCcw,
  Save,
  Sparkles,
  Users,
  CalendarDays,
  Lightbulb,
  Eye,
  RefreshCw,
  BadgeCheck,
} from "lucide-react";

const WEEKDAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const PLACEHOLDERS = ["{{campus}}", "{{school}}"];

const MEMBER_RANGES = [
  "1-10",
  "11-25",
  "26-50",
  "51-100",
  "101-250",
  "251-500",
  "500+",
];

/** Local echo of the backend's renderer, so the preview updates as you type. */
const render = (template: string, campus: string, school: string): string =>
  template
    .replace(/\{\{\s*campus\s*\}\}/g, campus)
    .replace(/\{\{\s*school\s*\}\}/g, school);

export function DefaultContentSettingsPage() {
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<DefaultContentSettings | null>(null);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  // Sample values the preview renders against.
  const [sampleCampus, setSampleCampus] = useState("MMC");
  const [sampleSchool, setSampleSchool] = useState(
    "Florida International University",
  );

  const { data, isLoading } = useQuery({
    queryKey: ["default-content-settings"],
    queryFn: defaultContentAPI.getSettings,
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (update: DefaultContentSettingsUpdate) =>
      defaultContentAPI.updateSettings(update),
    onSuccess: (saved) => {
      queryClient.setQueryData(["default-content-settings"], saved);
      setDraft(saved);
      setFeedback({ type: "success", message: "Settings saved." });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      setFeedback({
        type: "error",
        message: error.response?.data?.message || "Failed to save settings.",
      });
    },
  });

  const reapplyMutation = useMutation({
    mutationFn: defaultContentAPI.reapply,
    onSuccess: (counts) => {
      setFeedback({
        type: "success",
        message:
          `Applied to ${counts.accounts} Walky account${counts.accounts === 1 ? "" : "s"}, ` +
          `${counts.spaces} space${counts.spaces === 1 ? "" : "s"}, ` +
          `${counts.events} event${counts.events === 1 ? "" : "s"} and ` +
          `${counts.ideas} idea${counts.ideas === 1 ? "" : "s"}.`,
      });
    },
    onError: () => {
      setFeedback({ type: "error", message: "Could not apply to existing campuses." });
    },
  });

  const resetMutation = useMutation({
    mutationFn: defaultContentAPI.resetSettings,
    onSuccess: (fresh) => {
      queryClient.setQueryData(["default-content-settings"], fresh);
      setDraft(fresh);
      setResetOpen(false);
      setFeedback({ type: "success", message: "Restored the default copy." });
    },
  });

  if (isLoading || !draft) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  const patch = (update: DefaultContentSettingsUpdate) => {
    setDraft(
      (prev) =>
        prev && {
          ...prev,
          ...update,
          walky: { ...prev.walky, ...(update.walky ?? {}) },
          space: { ...prev.space, ...(update.space ?? {}) },
          event: { ...prev.event, ...(update.event ?? {}) },
          idea: { ...prev.idea, ...(update.idea ?? {}) },
        },
    );
    setFeedback(null);
  };

  // The upload endpoint writes the URL server-side and hands back the whole
  // settings row, so sync the draft from it rather than only patching the URL.
  const uploadTo =
    (slot: "walky-avatar" | "space-logo" | "space-cover" | "event-cover") =>
    async (file: File) => {
      const { url, data } = await defaultContentAPI.uploadImage(slot, file);
      queryClient.setQueryData(["default-content-settings"], data);
      setDraft(data);
      return url;
    };

  const preview = (template: string) =>
    render(template, sampleCampus || "Campus", sampleSchool || "School");

  const save = () =>
    saveMutation.mutate({
      enabled: draft.enabled,
      walky: draft.walky,
      space: draft.space,
      event: draft.event,
      idea: draft.idea,
    });

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Sparkles className="h-6 w-6" />
              Default Campus Content
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              What every new campus starts with, owned by the official Walky
              account. Use {PLACEHOLDERS.join(" and ")} anywhere in the text —
              they are filled in when the campus is created.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={() => reapplyMutation.mutate()}
              disabled={reapplyMutation.isPending}
              title="Re-render the templates onto campuses that were seeded earlier"
            >
              {reapplyMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Apply to existing
            </Button>
            <Button
              variant="outline"
              onClick={() => setResetOpen(true)}
              disabled={resetMutation.isPending}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button onClick={save} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        </div>

        {feedback && (
          <Alert variant={feedback.type === "error" ? "destructive" : "default"}>
            <AlertDescription>{feedback.message}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="flex items-center justify-between pt-6">
            <div>
              <p className="font-medium">Seed new campuses by default</p>
              <p className="text-sm text-muted-foreground">
                Pre-checks the box on the school form. It can still be turned off
                per school.
              </p>
            </div>
            <Switch
              checked={draft.enabled}
              onCheckedChange={(enabled) => patch({ enabled })}
            />
          </CardContent>
        </Card>

        <div className="grid gap-6 xl:grid-cols-[1fr_22rem]">
          <div className="space-y-6">
            {/* ------------------------------------------------------ walky */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BadgeCheck className="h-5 w-5" />
                  The Walky account
                </CardTitle>
                <CardDescription>
                  Owns everything seeded below, and is credited as the organizer.
                  There is one account, so a change here shows for every user.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="walky_name">Display name</Label>
                    <Input
                      id="walky_name"
                      value={draft.walky.first_name}
                      onChange={(e) =>
                        patch({ walky: { first_name: e.target.value } })
                      }
                    />
                  </div>
                  <ImageUrlField
                    id="walky_avatar"
                    label="Avatar"
                    value={draft.walky.avatar_url}
                    onChange={(url) => patch({ walky: { avatar_url: url } })}
                    onUpload={uploadTo("walky-avatar")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="walky_bio">Bio</Label>
                  <Textarea
                    id="walky_bio"
                    rows={2}
                    value={draft.walky.profile_bio}
                    onChange={(e) =>
                      patch({ walky: { profile_bio: e.target.value } })
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {/* ------------------------------------------------------ space */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="h-5 w-5" />
                  Welcome space
                </CardTitle>
                <CardDescription>
                  Always created. It also gets a group chat automatically.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="space_title">Title</Label>
                  <Input
                    id="space_title"
                    value={draft.space.title}
                    onChange={(e) => patch({ space: { title: e.target.value } })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="space_about">About</Label>
                  <Textarea
                    id="space_about"
                    rows={3}
                    value={draft.space.about}
                    onChange={(e) => patch({ space: { about: e.target.value } })}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <ImageUrlField
                    id="space_logo"
                    label="Logo"
                    value={draft.space.logo_url}
                    onChange={(url) => patch({ space: { logo_url: url } })}
                    onUpload={uploadTo("space-logo")}
                  />
                  <ImageUrlField
                    id="space_cover"
                    label="Cover image"
                    value={draft.space.cover_image_url}
                    onChange={(url) =>
                      patch({ space: { cover_image_url: url } })
                    }
                    onUpload={uploadTo("space-cover")}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="space_members">Member count range</Label>
                    <Select
                      value={draft.space.member_count_range}
                      onValueChange={(value) =>
                        patch({ space: { member_count_range: value } })
                      }
                    >
                      <SelectTrigger id="space_members">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MEMBER_RANGES.map((r) => (
                          <SelectItem key={r} value={r}>
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ------------------------------------------------------ event */}
            <Card>
              <CardHeader className="flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CalendarDays className="h-5 w-5" />
                    Welcome event
                  </CardTitle>
                  <CardDescription>
                    A weekly repeating series, so it never becomes a past event.
                  </CardDescription>
                </div>
                <Switch
                  checked={draft.event.enabled}
                  onCheckedChange={(enabled) => patch({ event: { enabled } })}
                />
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="event_name">Name</Label>
                  <Input
                    id="event_name"
                    value={draft.event.name}
                    disabled={!draft.event.enabled}
                    onChange={(e) => patch({ event: { name: e.target.value } })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event_description">Description</Label>
                  <Textarea
                    id="event_description"
                    rows={3}
                    value={draft.event.description}
                    disabled={!draft.event.enabled}
                    onChange={(e) =>
                      patch({ event: { description: e.target.value } })
                    }
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="event_day">Repeats on</Label>
                    <Select
                      value={String(draft.event.day_of_week)}
                      disabled={!draft.event.enabled}
                      onValueChange={(value) =>
                        patch({ event: { day_of_week: Number(value) } })
                      }
                    >
                      <SelectTrigger id="event_day">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WEEKDAYS.map((d) => (
                          <SelectItem key={d.value} value={String(d.value)}>
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event_hour">Start hour (0-23)</Label>
                    <Input
                      id="event_hour"
                      type="number"
                      min={0}
                      max={23}
                      value={draft.event.hour}
                      disabled={!draft.event.enabled}
                      onChange={(e) =>
                        patch({ event: { hour: Number(e.target.value) } })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event_duration">Duration (minutes)</Label>
                    <Input
                      id="event_duration"
                      type="number"
                      min={15}
                      max={1440}
                      value={draft.event.duration_minutes}
                      disabled={!draft.event.enabled}
                      onChange={(e) =>
                        patch({
                          event: { duration_minutes: Number(e.target.value) },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event_slots">Slots</Label>
                    <Input
                      id="event_slots"
                      type="number"
                      min={1}
                      max={10000}
                      value={draft.event.slots}
                      disabled={!draft.event.enabled}
                      onChange={(e) =>
                        patch({ event: { slots: Number(e.target.value) } })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event_type">Type</Label>
                    <Select
                      value={draft.event.event_type}
                      disabled={!draft.event.enabled}
                      onValueChange={(value) =>
                        patch({
                          event: { event_type: value as "outdoor" | "indoor" },
                        })
                      }
                    >
                      <SelectTrigger id="event_type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="outdoor">Outdoor</SelectItem>
                        <SelectItem value="indoor">Indoor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <ImageUrlField
                    id="event_image"
                    label="Cover image"
                    value={draft.event.image_url}
                    disabled={!draft.event.enabled}
                    onChange={(url) => patch({ event: { image_url: url } })}
                    onUpload={uploadTo("event-cover")}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  A campus with its own image uses that instead of this one.
                </p>
              </CardContent>
            </Card>

            {/* ------------------------------------------------------- idea */}
            <Card>
              <CardHeader className="flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Lightbulb className="h-5 w-5" />
                    Opening idea
                  </CardTitle>
                  <CardDescription>
                    Gives the Ideas tab something to answer on day one.
                  </CardDescription>
                </div>
                <Switch
                  checked={draft.idea.enabled}
                  onCheckedChange={(enabled) => patch({ idea: { enabled } })}
                />
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="idea_title">Title</Label>
                  <Input
                    id="idea_title"
                    value={draft.idea.title}
                    disabled={!draft.idea.enabled}
                    onChange={(e) => patch({ idea: { title: e.target.value } })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="idea_description">Description</Label>
                  <Textarea
                    id="idea_description"
                    rows={3}
                    value={draft.idea.description}
                    disabled={!draft.idea.enabled}
                    onChange={(e) =>
                      patch({ idea: { description: e.target.value } })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="idea_looking_for">Looking for</Label>
                  <Input
                    id="idea_looking_for"
                    value={draft.idea.looking_for}
                    disabled={!draft.idea.enabled}
                    onChange={(e) =>
                      patch({ idea: { looking_for: e.target.value } })
                    }
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ---------------------------------------------------- preview */}
          <div className="xl:sticky xl:top-6 xl:self-start">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Eye className="h-5 w-5" />
                  Preview
                </CardTitle>
                <CardDescription>
                  How it reads for a specific campus. Nothing is created.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="sample_campus" className="text-xs">
                      Sample campus
                    </Label>
                    <Input
                      id="sample_campus"
                      value={sampleCampus}
                      onChange={(e) => setSampleCampus(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sample_school" className="text-xs">
                      Sample school
                    </Label>
                    <Input
                      id="sample_school"
                      value={sampleSchool}
                      onChange={(e) => setSampleSchool(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-4 border-t pt-4 text-sm">
                  <div>
                    <Badge variant="secondary" className="mb-1.5">
                      Space
                    </Badge>
                    <p className="font-medium">{preview(draft.space.title)}</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      {preview(draft.space.about)}
                    </p>
                  </div>

                  <div className={draft.event.enabled ? "" : "opacity-40"}>
                    <Badge variant="secondary" className="mb-1.5">
                      Event {!draft.event.enabled && "(off)"}
                    </Badge>
                    <p className="font-medium">{preview(draft.event.name)}</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      {preview(draft.event.description)}
                    </p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Every{" "}
                      {WEEKDAYS.find(
                        (d) => d.value === draft.event.day_of_week,
                      )?.label ?? "—"}{" "}
                      at {String(draft.event.hour).padStart(2, "0")}:00 ·{" "}
                      {draft.event.duration_minutes} min · {draft.event.slots}{" "}
                      slots
                    </p>
                  </div>

                  <div className={draft.idea.enabled ? "" : "opacity-40"}>
                    <Badge variant="secondary" className="mb-1.5">
                      Idea {!draft.idea.enabled && "(off)"}
                    </Badge>
                    <p className="font-medium">{preview(draft.idea.title)}</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      {preview(draft.idea.description)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={() => resetMutation.mutate()}
        title="Restore the default copy?"
        description="Every field goes back to what Walky ships with. Content already created on existing campuses is not affected."
        variant="destructive"
      />
    </DashboardLayout>
  );
}

export default DefaultContentSettingsPage;
